import type React from 'react';
import TableActions from './actions';
import type { TableRowData, TableActionsCallback } from './actions';
import Style from './table.tailwind.js';
import { UMBadge } from '@urbanmetrix/um-kit';

type BadgeColor = 'red' | 'blue' | 'green' | 'orange';

interface RowActions {
  [key: string]: unknown;
}

interface RowDatum {
  id?: string | number;
  actions?: RowActions;
  [key: string]: unknown;
}

interface BadgeCondition {
  value: unknown;
  color: string;
}

interface BadgeConfig {
  col: string;
  color: string;
  condition?: BadgeCondition[];
}

interface TableCallback {
  edit: (res: unknown, row: RowDatum) => void;
  delete: (res: unknown, row: RowDatum) => void;
}

interface BodyProps {
  data?: RowDatum[];
  actions?: RowActions;
  callback: TableCallback;
  hide?: string[];
  show?: string[];
  badge?: BadgeConfig;
  customStyle?: boolean;
}

export function Body(props: BodyProps) {
  if (props.data?.length) {
    return (
      <tbody className={Style.body}>
        {props.data.map((row, index) => {
          return <Row {...props} data={row} key={index} />;
        })}
      </tbody>
    );
  }

  return (
    <tbody className={Style.body}>
      <tr>
        <td colSpan={10} className={Style.empty}>
          No results found
        </td>
      </tr>
    </tbody>
  );
}

interface RowProps {
  data: RowDatum;
  actions?: RowActions;
  callback: TableCallback;
  hide?: string[];
  show?: string[];
  badge?: BadgeConfig;
  customStyle?: boolean;
}

export function Row(props: RowProps) {
  let row = { ...props.data };
  row.actions = row.actions || props.actions;
  const hasActions = Object.values(row.actions || {}).some((x) => x !== undefined);
  return (
    <tr data-id={props.data.id}>
      {Object.keys(row).map((cell, index) => {
        // actions
        if (cell === 'actions')
          return hasActions ? (
            <TableActions
              row={row as TableRowData}
              index={index}
              key={index}
              callback={props.callback as unknown as TableActionsCallback}
            />
          ) : (
            false
          );

        // hide
        if (props.hide?.includes(cell)) return false;

        // show
        if (props.show && !props.show.includes(cell)) return false;

        let value = row[cell];

        // is date/time
        if (typeof value === 'string' && /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(value)) {
          const date = new Date(value).toISOString().split('T');
          value = `${date[0]} ${date[1].split('.')[0]}`;
        }

        // has badge
        if (value !== undefined && props.badge && cell === props.badge.col) {
          // default color
          let color = props.badge.color;

          // check each condition
          if (props.badge.condition) {
            props.badge.condition.forEach((cond) => {
              typeof cond.value === 'string' && typeof value === 'string'
                ? (color = cond.value.toLowerCase() === value.toLowerCase() ? cond.color : color)
                : (color = cond.value === value ? cond.color : color);
            });
          }

          return (
            <td key={index} className={Style.cell}>
              <UMBadge
                text={value === true ? 'Yes' : value === false ? 'No' : (value as React.ReactNode)}
                color={color as BadgeColor}
                className={Style.badge}
              />
            </td>
          );
        }

        // standard cell
        return (
          <td key={index} className={props.customStyle ? Style.customCell : Style.cell}>
            {value === true ? 'Yes' : value === false ? 'No' : (value as React.ReactNode)}
          </td>
        );
      })}
    </tr>
  );
}
